import * as React from "react";
import Typography from "@mui/material/Typography";
import { Calendar, Info, Video, ExternalLink } from "lucide-react";
import { timeAgo } from "components/common/utils";
import { getSchoolClassLabel } from "../../common/utils";

interface RequestListItemProps {
  request: any;
  index: number;
  onProposeSlots: (request: any) => void;
  onViewDetails?: (request: any) => void;
  onAddMeetLink?: (request: any) => void;
  getStatusBadgeColor: (status: string) => string;
  getStatusText: (status: string) => string;
  searchQuery?: string;
}

const RequestListItem: React.FC<RequestListItemProps> = ({
  request,
  index,
  onProposeSlots,
  onViewDetails,
  onAddMeetLink,
  getStatusBadgeColor,
  getStatusText,
  searchQuery = "",
}) => {
  return (
    <div className="flex flex-col lg:grid lg:grid-cols-12 lg:gap-4 p-4 sm:p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 relative group border border-gray-200">
      {/* Mobile: Layout en colonne, Desktop: Layout en grille */}
      <div className="flex-1 mb-4 lg:mb-0 lg:col-span-5">
        {/* En-tête avec titre et statut */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
          <Typography
            variant="body1"
            className="font-semibold text-base sm:text-lg text-gray-900"
          >
            Explication mathématique
          </Typography>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium w-fit ${getStatusBadgeColor(
              request.status,
            )}`}
          >
            {getStatusText(request.status)}
          </span>
        </div>

        {/* Classe */}
        <div className="mb-3">
          <Typography
            variant="body2"
            className="text-gray-600 text-sm font-medium"
          >
            {getSchoolClassLabel(request.schoolClass || request.studentClass)}
          </Typography>
        </div>

        {/* Description */}
        {request.comment && (
          <div className="mb-3">
            <Typography
              variant="body2"
              className="text-gray-700 text-sm leading-relaxed line-clamp-2"
            >
              {request.comment.substring(0, 120)}...
            </Typography>
          </div>
        )}

        {/* Lien Meet si disponible */}
        {(request.status === "student_confirmed" ||
          request.status === "meeting_scheduled" ||
          request.status === "scheduled") && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3">
              <Video className="w-4 h-4 text-blue-600 flex-shrink-0" />
              {request.meeting && request.meeting.link ? (
                <>
                  <span className="text-sm font-medium text-blue-900">
                    Lien Meet disponible
                  </span>
                  <a
                    href={request.meeting.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                    title="Ouvrir le lien Meet"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </>
              ) : (
                <span className="text-sm font-medium text-blue-900">
                  Séance confirmée - Ajouter le lien Meet
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center lg:col-span-7 gap-4">
        {/* Date */}
        <div className="lg:col-span-2">
          <Typography variant="body2" className="text-gray-500 text-sm">
            {timeAgo(new Date(request.createdAt))}
          </Typography>
        </div>

        {/* Actions */}
        <div className="flex gap-2 lg:col-span-5 lg:justify-end">
          {request.status === "validated" && (
            <button
              onClick={() => onProposeSlots(request)}
              className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm font-medium"
              title="Proposer créneaux"
            >
              <Calendar size={16} />
              <span className="hidden sm:inline">Proposer créneaux</span>
            </button>
          )}

          {request.status === "student_confirmed" && onAddMeetLink && (
            <button
              onClick={() => onAddMeetLink(request)}
              className="flex items-center gap-2 px-3 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors text-sm font-medium"
              title="Ajouter/Modifier lien Meet"
            >
              <Video size={16} />
              <span className="hidden sm:inline">Lien Meet</span>
            </button>
          )}

          {onViewDetails && (
            <button
              onClick={() => onViewDetails(request)}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
              title="Voir les détails"
            >
              <Info size={16} />
              <span className="hidden sm:inline">Détails</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface AssignedRequestsProps {
  requests: any[];
  onProposeSlots: (request: any) => void;
  onCreateMeeting?: (request: any) => void;
  onViewDetails?: (request: any) => void;
  onAddMeetLink?: (request: any) => void;
  getStatusBadgeColor: (status: string) => string;
  getStatusText: (status: string) => string;
}

const AssignedRequests: React.FC<AssignedRequestsProps> = ({
  requests,
  onProposeSlots,
  onViewDetails,
  onAddMeetLink,
  getStatusBadgeColor,
  getStatusText,
}) => {
  if (requests.length === 0) {
    return (
      <p className="text-gray-500 text-center text-sm">
        Aucune demande trouvée.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {requests.map((request, index) => (
        <RequestListItem
          key={request._id}
          request={request}
          index={index}
          onProposeSlots={onProposeSlots}
          onViewDetails={onViewDetails}
          onAddMeetLink={onAddMeetLink}
          getStatusBadgeColor={getStatusBadgeColor}
          getStatusText={getStatusText}
          searchQuery=""
        />
      ))}
    </div>
  );
};

export default AssignedRequests;
